import { FormTitle, message, Modal, Tabs, useBreadcrumbRoutes, Wrapper } from '@maxtropy/components';
import { Spin, TabsProps } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import LoadSideParamsTab, { LoadSideParamsTabRef } from './components/LoadSideParamsTab';
import MaintenanceMonthTab, { MaintenanceMonthTabRef } from './components/MaintenanceMonthTab';
import MicrogridTopologyTab, { MicrogridTopologyTabRef } from './components/MicrogridTopologyTab';
import PvParamsTab, { PvParamsTabRef } from './components/PvParamsTab';
import ThermalPowerParamsTab, { ThermalPowerParamsTabRef } from './components/ThermalPowerParamsTab';
import styles from './index.module.scss';

const tabTitleMap: Record<string, string> = {
  topology: '微电网拓扑建模',
  loadSide: '负荷侧-炉台参数配置',
  thermalPower: '火电侧发电功率参数配置',
  pv: '光伏减排参数配置',
  maintenanceMonth: '检修月配置',
};

type TabRef =
  | MicrogridTopologyTabRef
  | LoadSideParamsTabRef
  | ThermalPowerParamsTabRef
  | PvParamsTabRef
  | MaintenanceMonthTabRef;

const MicrogridTopologyModeling = () => {
  const routesContext = useBreadcrumbRoutes();
  const [currentTab, setCurrentTab] = useState('topology');
  const tabRefs = useRef<Record<string, TabRef | null>>({});
  const savingRef = useRef(false);

  const tabItems: TabsProps['items'] = useMemo(
    () => [
      {
        key: 'topology',
        label: '微电网拓扑建模',
      },
      {
        key: 'loadSide',
        label: '负荷侧参数配置',
      },
      {
        key: 'thermalPower',
        label: '火电侧参数配置',
      },
      {
        key: 'pv',
        label: '光伏侧参数配置',
      },
      {
        key: 'maintenanceMonth',
        label: '检修月配置',
      },
    ],
    []
  );

  const attachTabRef = useCallback(
    (key: string) => (node: TabRef | null) => {
      tabRefs.current[key] = node;
    },
    []
  );

  const onTabChange = (key: string) => {
    const ref = tabRefs.current[currentTab];

    if (!ref?.isEditing) {
      setCurrentTab(key);

      return;
    }

    Modal.confirm({
      title: '当前有未保存的编辑内容',
      content: '是否保存后再切换？',
      okText: '保存',
      cancelText: '取消',
      onOk: async () => {
        if (savingRef.current) {
          return;
        }

        savingRef.current = true;
        try {
          await ref.save();
          setCurrentTab(key);
        } catch (error) {
          // 保存失败：不切换，留在当前 tab，用户可查看表单校验错误后重试
          message.error(error instanceof Error ? error.message : '保存失败，请重试');
        } finally {
          savingRef.current = false;
        }
      },
    });
  };

  return (
    <Wrapper routes={routesContext?.routes} className={styles.wrapper}>
      <Tabs activeKey={currentTab} items={tabItems} selectedLinePlacement="bottom" bordered onChange={onTabChange} />
      <div className={styles.content}>
        <FormTitle title={tabTitleMap[currentTab]} />
        {currentTab === 'topology' && <MicrogridTopologyTab ref={attachTabRef('topology')} />}
        {currentTab === 'loadSide' && <LoadSideParamsTab ref={attachTabRef('loadSide')} />}
        {currentTab === 'thermalPower' && <ThermalPowerParamsTab ref={attachTabRef('thermalPower')} />}
        {currentTab === 'pv' && <PvParamsTab ref={attachTabRef('pv')} />}
        {currentTab === 'maintenanceMonth' && <MaintenanceMonthTab ref={attachTabRef('maintenanceMonth')} />}
      </div>
    </Wrapper>
  );
};

export default MicrogridTopologyModeling;
